import React, { useCallback, useState } from 'react';
import { ChevronLeft } from 'lucide-react';
import { EchoesOfEmpire } from './Provenance';
import { EmojiArt } from '../EmojiArt';
import { deckForRound } from '../../data/blissEchoDecks';
import { useHistoryClose } from '../../hooks/useHistoryClose';

const GOLD = '#C5A059';
const GOLD_HI = '#E9C77A';
const ROUND_KEY = 'echo_round_idx';

/* ═══════════════════════════════════════════════════════════════════
   THE ECHO — pic-to-pic memory match, category auto-cycles round by
   round via localStorage. 10 categories × 2 cycles (fresh emojis after
   round 10). Reshuffle same board or Change to next category.
   ═══════════════════════════════════════════════════════════════════ */

const readRound = () => {
  try {
    const v = parseInt(window.localStorage.getItem(ROUND_KEY) || '0', 10);
    return Number.isFinite(v) && v >= 0 ? v : 0;
  } catch (_) { return 0; /* localStorage blocked */ }
};
const writeRound = (n) => {
  try { window.localStorage.setItem(ROUND_KEY, String(n)); } catch (_) { /* noop */ }
};

export const TheEcho = ({ onClose }) => {
  const [roundIdx, setRoundIdx] = useState(readRound());
  const [inGame, setInGame] = useState(false);

  // Force EchoesOfEmpire to remount when category changes so it re-deals.
  const [gameKey, setGameKey] = useState(0);

  useHistoryClose(true, useCallback(() => {
    if (inGame) {
      setInGame(false);
      try { window.history.pushState({ __modal: Date.now() }, ''); } catch (_) { /* noop */ }
    } else {
      onClose();
    }
  }, [inGame, onClose]));

  const deck = deckForRound(roundIdx);
  const nextDeck = deckForRound(roundIdx + 1);

  const startGame = () => {
    setGameKey((k) => k + 1);
    setInGame(true);
  };

  const changeCategory = () => {
    const next = roundIdx + 1;
    writeRound(next);
    setRoundIdx(next);
    setGameKey((k) => k + 1);
    // stay in game — EchoesOfEmpire remounts with the new deck
  };

  return (
    <div className="fixed inset-0 z-[220] flex flex-col overflow-hidden"
      style={{ background: '#050308' }}
      data-testid="echo-modal">
      {/* Header */}
      <div className="flex items-center justify-between px-3 py-2.5 flex-shrink-0 border-b"
        style={{ borderColor: `${GOLD}22` }}>
        <button onClick={inGame ? () => setInGame(false) : onClose}
          data-testid="echo-close-btn"
          className="relative z-10 flex-shrink-0 h-8 px-3 rounded-full flex items-center gap-1 whitespace-nowrap active:scale-95 transition-transform"
          style={{ border: '1px solid #B57EDC8C', background: '#B57EDC14', boxShadow: '0 0 10px #B57EDC40', color: '#B57EDC' }}>
          <ChevronLeft size={16}/>
          <span className="font-['Outfit'] uppercase" style={{ fontSize: 11, letterSpacing: '0.18em' }}>Back</span>
        </button>
        <p className="font-['Cinzel'] tracking-[0.22em]"
          style={{ color: '#FFE9A0', fontSize: 13, textShadow: '0 0 10px rgba(197,160,89,0.35)' }}>
          {inGame ? `ECHO · ${deck.label.toUpperCase()}` : 'ECHO'}
        </p>
        <span className="w-16"/>
      </div>

      {inGame ? (
        <div className="flex-1 min-h-0 overflow-hidden flex flex-col">
          <EchoesOfEmpire
            key={gameKey}
            onBack={() => setInGame(false)}
            memories={[]}
            blissDeck={deck}
            hostedInModal={true}
            onChangeCategory={changeCategory}/>
        </div>
      ) : (
        <div className="flex-1 min-h-0 overflow-y-auto px-6 py-8 flex flex-col items-center justify-center text-center"
          data-testid="echo-picker">
          <p className="font-['Cinzel'] tracking-[0.24em] mb-1"
            style={{ color: '#FFE9A0', fontSize: 22, textShadow: `0 0 18px ${deck.accent}88` }}
            data-testid="echo-current-category">
            {deck.label.toUpperCase()}
          </p>
          <p className="font-['Cormorant_Garamond'] italic mb-8"
            style={{ color: 'rgba(255,255,255,0.55)', fontSize: 14 }}>
            Match each symbol to its twin.
          </p>

          {/* Sample orb */}
          <div className="rounded-2xl flex items-center justify-center mb-8"
            style={{
              width: 160, height: 160,
              background: `radial-gradient(circle at 40% 40%, ${deck.accent}33, rgba(5,3,8,0.98))`,
              border: `1.5px solid ${deck.accent}`,
              boxShadow: `0 0 32px ${deck.accent}66, inset 0 0 30px ${deck.accent}22`,
            }}>
            <EmojiArt emoji={deck.entries[0].emoji} size={90}/>
          </div>

          <button type="button"
            onClick={startGame}
            data-testid="echo-deal-board"
            className="px-8 py-3.5 rounded-full font-['Cinzel'] uppercase tracking-[0.3em]"
            style={{
              background: `linear-gradient(135deg, ${GOLD_HI}, ${GOLD})`,
              color: '#0a0610', fontSize: 13,
              boxShadow: `0 0 28px ${GOLD}77`,
            }}>
            Deal the Board
          </button>

          <p className="font-['Outfit'] uppercase tracking-[0.28em] mt-6"
            style={{ color: 'rgba(255,255,255,0.35)', fontSize: 9 }}>
            Next → {nextDeck.label}
          </p>
        </div>
      )}
    </div>
  );
};

export const EchoTileIcon = ({ size = 26 }) => (
  /* ── ECHO — two mirrored cards linked by an echo arc, signalling the
        pic-to-pic memory-match gameplay. */
  <svg width={size} height={size} viewBox="0 0 28 28" fill="none"
    style={{ filter: 'drop-shadow(0 0 8px rgba(197,160,89,0.6))' }}>
    <defs>
      <linearGradient id="echoTileGrad" x1="0" x2="0" y1="0" y2="1">
        <stop offset="0" stopColor="#F0DAA3"/>
        <stop offset="0.6" stopColor="#C5A059"/>
        <stop offset="1" stopColor="#7A5722"/>
      </linearGradient>
    </defs>
    {/* Left card */}
    <rect x="3.5" y="6" width="9" height="14" rx="1.4"
      fill="none" stroke="url(#echoTileGrad)" strokeWidth="1.3"/>
    <circle cx="8" cy="13" r="1.8" fill="url(#echoTileGrad)"/>
    {/* Right card (mirrored, same size) */}
    <rect x="15.5" y="6" width="9" height="14" rx="1.4"
      fill="none" stroke="url(#echoTileGrad)" strokeWidth="1.3"/>
    <circle cx="20" cy="13" r="1.8" fill="url(#echoTileGrad)"/>
    {/* Echo/link arc connecting the two cards + small ripple */}
    <path d="M11 13 Q14 8 17 13" fill="none" stroke="url(#echoTileGrad)"
      strokeWidth="1.1" strokeLinecap="round" opacity="0.9"/>
    <path d="M11 13 Q14 6 17 13" fill="none" stroke="url(#echoTileGrad)"
      strokeWidth="0.7" strokeLinecap="round" opacity="0.45"/>
  </svg>
);

export default TheEcho;
